import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { Pagination } from '~/Api/Pagination';
import { Item, Product, ProductList, ProductListFolder, ProductsStats, Query, QueryErrors } from '~/Api/Product';
import GroupProductActionTypes from '~/GroupProduct/ActionTypes';
import { Action as GroupProductAction } from '~/GroupProduct/reducer';
import FilterParameterDescription from '~/utils/FilterParameter/FilterParameterDescription';

import { Action, ActionType } from './Action';

export interface ProductsState {
	request: Promise<any>;
	isFetching: boolean;
	items: Item[];
	pagination: Pagination | null;
	paginationLimitReached: boolean | null;
	stats: ProductsStats | null;
}

export interface State {
	byQuery: { [key in string]: ProductsState };
	byId: { [key in string]: Product | null | undefined }; // tslint:disable-line: no-null-undefined-union
	availableParameters: FilterParameterDescription<string | string[]>[];
	queryErrors: { query: Query; errors: QueryErrors } | null;
	productRequest: { id: string; request: Promise<any> } | null;
	showAllProducts: boolean;
}

export default function reducer(
	state: State = createState(),
	action: Action | AccessTokenAction | GroupProductAction
): State {
	switch (action.type) {
		case ActionType.requestProducts: {
			const productsState: ProductsState | null = state.byQuery[action.query.key];

			return {
				...state,
				byQuery: {
					...state.byQuery,
					[action.query.key]: {
						request: action.request,
						isFetching: true,
						items: productsState?.items || [],
						pagination: productsState?.pagination || null,
						paginationLimitReached: null,
						stats: productsState?.stats || null,
					},
				},
			};
		}

		case ActionType.receiveProducts: {
			const productsState = state.byQuery[action.query.key];
			if (!productsState || productsState.request !== action.request) {
				return state;
			}

			if (action.paginationLimitReached) {
				return {
					...state,
					byQuery: {
						...state.byQuery,
						[action.query.key]: {
							...productsState,
							isFetching: false,
							paginationLimitReached: action.paginationLimitReached,
						},
					},
				};
			}

			if (action.queryErrors) {
				const newByQuery = { ...state.byQuery };
				delete newByQuery[action.query.key];

				return {
					...state,
					byQuery: newByQuery,
					queryErrors: {
						query: action.query,
						errors: action.queryErrors,
					},
				};
			}

			if (action.items === null) {
				return state;
			}

			const { query, items, pagination } = action;

			const newItems = [...productsState.items];
			newItems.splice(pagination.getFirstIndex(), pagination.perPage, ...items);

			const byId = { ...state.byId };
			items.forEach((item) => (byId[item.product.id] = item.product));

			return {
				...state,
				byQuery: {
					...state.byQuery,
					[query.key]: {
						...productsState,
						isFetching: false,
						items: newItems,
						pagination,
						paginationLimitReached: action.paginationLimitReached,
						stats: action.stats,
					},
				},
				byId,
				queryErrors: action.queryErrors
					? {
							query,
							errors: action.queryErrors,
					  }
					: null,
			};
		}

		case ActionType.pruneProducts: {
			const byQuery = { ...state.byQuery };
			delete byQuery[action.query.key];

			return {
				...state,
				byQuery,
			};
		}

		case ActionType.setQueryErrors:
			return {
				...state,
				queryErrors: {
					query: action.query,
					errors: action.errors,
				},
			};

		case ActionType.requestProduct:
			return action.request
				? {
						...state,
						productRequest: { id: action.id, request: action.request },
				  }
				: state;

		case ActionType.receiveProduct: {
			const productRequest =
				action.request && action.request === state.productRequest?.request ? null : state.productRequest;

			return {
				...state,
				byId: {
					...state.byId,
					[action.id]: action.product,
				},
				productRequest,
			};
		}

		case ActionType.showAllProductsAction:
			return { ...state, showAllProducts: action.showAll };

		case GroupProductActionTypes.receiveAddToGroup: {
			const { group, products } = action;
			const productIds = new Set(products.map((product) => product.id));

			return {
				...state,
				byQuery: updateByQuery(state.byQuery, (item) => {
					return productIds.has(item.product.id) && !item.memberOfListsHasListWithId(group.id)
						? item.memberOfListsAdded(
								new ProductList(
									group.id,
									group.name,
									group.parent ? new ProductListFolder(group.parent.id, group.parent.name) : null
								)
						  )
						: item;
				}),
			};
		}

		case GroupProductActionTypes.receiveAddToGroupBySearch: {
			const { group } = action;

			return {
				...state,
				byQuery: updateByQuery(state.byQuery, (item) => {
					return !item.memberOfListsHasListWithId(group.id)
						? item.memberOfListsAdded(
								new ProductList(
									group.id,
									group.name,
									group.parent ? new ProductListFolder(group.parent.id, group.parent.name) : null
								)
						  )
						: item;
				}),
			};
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

function createState(): State {
	return {
		byQuery: {},
		byId: {},
		availableParameters: [
			new FilterParameterDescription('all', 'All', false, 'text', ''),
			new FilterParameterDescription('brandName', 'Brand Name', false, 'text', ''),
			new FilterParameterDescription('atc', 'ATC', false, 'text', ''),
			new FilterParameterDescription('mah', 'Company', false, 'text', ''),
			new FilterParameterDescription('activeSubstance', 'Active Substance', false, 'text', ''),
			new FilterParameterDescription('country', 'Country', false, 'text', []),
			new FilterParameterDescription('newerThan', 'Newer than', false, 'date', ''),
			new FilterParameterDescription('datasource', 'Datasource', false, 'text', []),
			new FilterParameterDescription('pricesPresence', 'Prices presence', false, 'text', ''),
			new FilterParameterDescription('productDatasetPresence', 'Product dataset presence', false, 'text', ''),
		],
		queryErrors: null,
		productRequest: null,
		showAllProducts: false,
	};
}

function updateByQuery(
	byQuery: { [key in string]: ProductsState },
	updater: (item: Item) => Item
): { [key in string]: ProductsState } {
	const newByQuery = { ...byQuery };

	Object.keys(newByQuery).forEach((key) => {
		newByQuery[key] = {
			...newByQuery[key],
			items: newByQuery[key].items.map(updater),
		};
	});

	return newByQuery;
}

export function selectProductsByQuery(state: State, query: Query): ProductsState | null {
	return state.byQuery[query.key] ?? null;
}

export function selectProductById(state: State, id: string | null): Product | null | undefined {
	return id ? state.byId[id] : null;
}

export function selectQueryErrors(state: State, query: Query): QueryErrors | null {
	return state.queryErrors && state.queryErrors.query.equals(query) ? state.queryErrors.errors : null;
}
